import { ProLayout } from '@ant-design/pro-layout';
import { type TFunction } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ApplicationStatus } from '../../../../api/types';
import Footer from '../../../../components/Footer';
import BasicProLayoutContainer from '../../../../layouts/BasicProLayout/BasicProLayoutContainer';
import HeaderLogo from '../../../../layouts/HeaderLogo';
import { type MobilityApplicationStateWithExpired } from '../helper';
import ThankYouContent from './ThankYouContent';

const StyledProLayout = styled(ProLayout)`
    .ant-layout {
        background-color: #ffffff;
    }

    .ant-pro-global-header {
        padding: 0 24px;
    }
`;

const Container = styled.div`
    & .ant-page-header {
        display: none;
    }
`;

const footerRender = () => <Footer themeSpecific />;

const headerContentRender = () => <HeaderLogo link="/" />;

const getSubtitleText = (invalidStatus: boolean, isCancel: boolean, t: TFunction) => {
    if (invalidStatus) {
        return t('mobilityThankYou:subTitles.alreadyCancelled');
    }

    return isCancel ? t('mobilityThankYou:subTitles.cancelFailed') : t('mobilityThankYou:subTitles.amendFailed');
};

const getDescriptionText = (invalidStatus: boolean, isCancel: boolean, t: TFunction) => {
    if (invalidStatus) {
        return t('mobilityThankYou:descriptions.alreadyCancelled');
    }

    return isCancel ? t('mobilityThankYou:descriptions.cancelFailed') : t('mobilityThankYou:descriptions.amendFailed');
};

const isValidStatusToAmendOrCancel = (application: MobilityApplicationStateWithExpired) =>
    ![ApplicationStatus.Cancelled, ApplicationStatus.Declined, ApplicationStatus.Completed].some(
        status => status === application.mobilityStage?.status
    );

export type InvalidBookingResultProps = {
    isCancel?: boolean;
    application: MobilityApplicationStateWithExpired;
};

const InvalidBookingResult = ({ isCancel, application }: InvalidBookingResultProps) => {
    const { t } = useTranslation(['mobilityThankYou']);

    const externalModelInfoBaseUrl = useMemo(() => {
        if (application.module.__typename === 'MobilityModule' && application.module.externalModelInfo) {
            return application.module.baseUrl;
        }

        return null;
    }, [application]);

    const thankYouContent = useMemo(() => {
        const invalidStatus = !isValidStatusToAmendOrCancel(application);
        const title = invalidStatus ? t('mobilityThankYou:titles.cancel') : t('mobilityThankYou:titles.fail');
        const subTitle = getSubtitleText(invalidStatus, isCancel, t);
        const description = getDescriptionText(invalidStatus, isCancel, t);

        return { title, subTitle, description, application, externalModelInfoBaseUrl };
    }, [isCancel, application, externalModelInfoBaseUrl, t]);

    const menuRender = useCallback(() => <div />, []);

    return (
        <StyledProLayout
            footerRender={footerRender}
            headerContentRender={headerContentRender}
            headerHeight={80}
            headerTheme="light"
            layout="mix"
            logo={null}
            menuRender={menuRender}
            title={false}
        >
            <Container>
                <BasicProLayoutContainer>
                    <BasicProLayoutContainer>
                        <ThankYouContent {...thankYouContent} />
                    </BasicProLayoutContainer>
                </BasicProLayoutContainer>
            </Container>
        </StyledProLayout>
    );
};

export default InvalidBookingResult;
